import { FilterType } from 'library/Types';
import { MasterConfig, ConsignmentBucketKeys, FilterMetadata } from '../../types/master-data-types';
import moment from 'moment';
import { snakeToPretty } from 'library/lodash-helper';
import { DetailsToShowKeysIntl, DetailsToShowKeysDomestic } from './consignment-details.constants';
import Helper from '../../library/Helper';
import { message } from 'antd';
import {
    printConsignment,
    printConsignment4x6,
    printConsignment4x6Domestic,
    downloadConsignments4X6Files,
    printInvoice,
} from '../../network/consignments.api';

export const buildDeliverySlotTimeFilter = (deliverySlotValue: any) => {
    if (!deliverySlotValue) {
        return null;
    }

    const start = deliverySlotValue.startDate;
    const end = deliverySlotValue.endDate;

    if (!start || !end) {
        return null;
    }

    return {
        columnName: 'delivery_time_slot',
        fromDateString: start,
        toDateString: end,
    };
};

export const getFilterMetadataInOrder = (
    config: MasterConfig,
    filterIds: string[],
) => {
    if (!config?.filter_metadata || !filterIds?.length) {
        return [];
    }
    const seen = new Set<string>();
    const orderedIds = filterIds.filter((id) => {
        if (!id || seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    });

    return orderedIds
        .map((id) => config.filter_metadata?.find((filter) => filter.id === id))
        .filter((filter): filter is FilterMetadata => Boolean(filter));
};

export const getFilters = (
    bucket: ConsignmentBucketKeys,
    config: MasterConfig,
    visibleFilterIds?: string[],
) => {
    if (!bucket || !config || !config.filter_list_by_bucket || !config.filter_metadata) {
        return [];
    }
    const enabledFilters = config?.filter_list_by_bucket[bucket] || [];
    const filterIdList = visibleFilterIds?.slice() || enabledFilters.map((filter) => filter.id);

    const filtersToShow = getFilterMetadataInOrder(config, filterIdList);
    filtersToShow.push({
        id: 'cncFlag',
        mode: undefined,
        label: 'C&C Flag',
        placeholder: 'C&C Flag',
        type: FilterType.select,
        options: [{
            label: 'ALL',
            value: 'all',
        }, {
            label: 'True',
            value: 'true',
        }, {
            label: 'False',
            value: 'false',
        }],
    });
    return filtersToShow;
};

// Status color mapping
export const STATUS_COLOR_MAP: Record<string, { color: string; background: string }> = {
    // Blue
    created: { color: '#1677ff', background: '#e6f4ff' },
    softdata_upload: { color: '#1677ff', background: '#e6f4ff' },
    pickup_scheduled: { color: '#1677ff', background: '#e6f4ff' },
    pickup_awaited: { color: '#1677ff', background: '#e6f4ff' },
    out_for_pickup: { color: '#1677ff', background: '#e6f4ff' },
    rto: { color: '#1677ff', background: '#e6f4ff' },

    // Yellow
    pickup_completed: { color: '#bfa100', background: '#fffbe6' },
    reachedathub: { color: '#bfa100', background: '#fffbe6' },
    inscan_at_hub: { color: '#bfa100', background: '#fffbe6' },
    intransittohub: { color: '#bfa100', background: '#fffbe6' },
    handover_courier_partner: { color: '#bfa100', background: '#fffbe6' },
    in_transit: { color: '#bfa100', background: '#fffbe6' },
    outfordelivery: { color: '#bfa100', background: '#fffbe6' },
    rto_outfordelivery: { color: '#bfa100', background: '#fffbe6' },

    // Green
    delivered: { color: '#389e0d', background: '#f6ffed' },
    rto_delivered: { color: '#389e0d', background: '#f6ffed' },

    // Red
    not_picked_up: { color: '#cf1322', background: '#fff1f0' },
    attempted: { color: '#cf1322', background: '#fff1f0' },
    cancelled: { color: '#cf1322', background: '#fff1f0' },
    rto_attempted: { color: '#cf1322', background: '#fff1f0' },
};

export const formatDateTime = (epochTime : number, type: string, format?: string) => {
    if (!epochTime) return 'N/A';
    let dateTime;
    if (epochTime.toString().length < 13) {
        dateTime = moment(new Date(Number(epochTime) * 1000));
    } else {
        dateTime = moment(new Date(Number(epochTime)));
    }
    if (!dateTime.isValid()) return 'N/A';
    if (type === 'date') {
        return dateTime.format('DD MMM YYYY');
    }
    if (type === 'time') {
        return dateTime.format('HH:mm:ss');
    }
    return dateTime.format(format || 'DD MMM YYYY, hh:mm:ss A');
};

/**
 * Get pretty field name with same label transformation logic as consignment details
 * This ensures consistent field names across details page and gear modal
 * @param key - The field key
 * @param t - Translation function
 * @param i18n - i18n instance
 * @param viewDataKeyLabelMap - Optional custom label map from view data
 * @returns Transformed and translated label
 */
export const getFieldPrettyName = (
    key: string,
    t: any,
    i18n: any,
    viewDataKeyLabelMap?: Record<string, string>,
) => {
    let label = snakeToPretty(key);

    // If custom view label exists, use it
    if (viewDataKeyLabelMap && viewDataKeyLabelMap[key]) {
        return viewDataKeyLabelMap[key];
    }

    // Apply same switch case transformations as labelValuePair in consignment-details-international-new.tsx
    switch (key) {
        case 'load_type':
            label = 'Item Type';
            break;
        case 'num_pieces':
            label = 'No. of Pieces';
            break;
        case 'meis':
            label = 'MEIS';
            break;
        case 'is_risk_surcharge_applicable':
            label = 'Risk Surcharge';
            break;
        case 'sender_service_time_mins':
            label = 'Sender Service Time (mins)';
            break;
        case 'destination_service_time_mins':
            label = 'Destination Service Time (mins)';
            break;
        case 'sender_pincode':
            label = label.replace('Sender', 'Origin');
            break;
        case 'origin_type':
            label = label.replace('Origin', 'Sender');
            break;
        case 'against_bond_lut':
            label = 'Against Bond LUT';
            break;
        case 'international_detail_shipment_purpose':
            label = 'Shipment Purpose';
            break;
        case 'delivery_terms':
            label = 'Shipment Terms';
            break;
        case 'consignor_pan':
            label = 'Consignor Pan Id';
            break;
        case 'booking_service_type_id':
            label = 'Booking Service Type';
            break;
        case 'courier_partner_reference_number':
            label = 'Carrier AWB No.';
            break;
        case 'courier_partner':
            label = 'Carrier';
            break;
        case 'transport_mode':
            label = 'Transport Mode';
            break;
        case 'document_list':
            label = 'document_list';
            break;
        case 'price_details':
            label = 'Customer Charges';
            break;
        case 'is_stackable':
            label = 'Is Stackable';
            break;
        case 'is_dangerous_good':
            label = 'Is Dangerous Good';
            break;
        case 'dangerous_good_accessible':
            label = 'Dangerous Goods Accessible';
            break;
        case 'dangerous_good_un_number':
            label = 'Dangerous Good UN Number';
            break;
        case 'dangerous_good_name':
            label = 'Dangerous Good Name';
            break;
        case 'dangerous_good_class':
            label = 'Dangerous Good Class';
            break;
        case 'dangerous_good_division':
            label = 'Dangerous Good Division';
            break;
        case 'dangerous_good_category':
            label = 'Dangerous Good Category';
            break;
        case 'dangerous_good_packing_group':
            label = 'Dangerous Good Packing Group';
            break;
        case 'dangerous_good_category_type':
            label = 'Dangerous Good Category Type';
            break;
        case 'dangerous_good_category_packing_instruction':
            label = 'Dangerous Good Packing Instruction';
            break;
        case 'forward_reference_number':
            label = 'Forward Reference Number';
            break;
        case 'reverse_reference_number':
            label = 'Reverse Reference Number';
            break;
        case 'igst':
            label = 'IGST';
            break;
        case 'cgst':
            label = 'CGST';
            break;
        case 'sgst':
            label = 'SGST';
            break;
        case 'tax_details_receiver_gstin':
            label = 'Reciever GSTIN';
            break;
        case 'tax_details_sender_gstin':
            label = 'Sender GSTIN';
            break;
        case 'total_tax':
            label = 'Total Tax';
            break;
        case 'tax_percentage':
            label = 'Tax Percentage';
            break;
        case 'import_reference_number':
            label = 'Import Reference Number';
            break;
        case 'export_reference_number':
            label = 'Export Reference Number';
            break;
        case 'expected_delivery_date':
            label = 'Expected Delivery Date';
            break;
        default:
            break;
    }

    // Apply i18n translation if available (same logic as labelValuePair)
    if (i18n.exists(key) && label?.trim() !== '') {
        label = t(key);
    }

    return label;
};

// Types for metadata generation
interface ColumnInfo {
    pretty_name: string;
    id: string;
    key: string;
    type: string;
}

export interface ColumnMetadata {
    tab_header: string;
    details: Array<{
        header: string;
        columns: ColumnInfo[];
    }>;
}

/**
 * Generate details metadata from constants using same label logic as details page
 * This ensures gear modal has same field names as details page
 */
export const generateDetailsMetadata = (isInternational: boolean, t: any, i18n: any): ColumnMetadata[] => {
    const baseKeys = isInternational ? DetailsToShowKeysIntl : DetailsToShowKeysDomestic;

    // Map section keys to tab headers and section headers (for nested groups)
    const sectionToTabHeader: Record<string, string> = {
        shipmentDetailsKeys: 'Primary Details',
        taxAndCustomDetailsKeys: 'Primary Details',
        packageDetailsKeys: 'Primary Details',
        codDetailsKeys: 'Primary Details',
        billingDetailsKeys: 'Primary Details',
        dangerousGoodKeys: 'Primary Details',
        pieceDetails: 'Piece Details',
        itemDetails: 'Piece Details',
        consigneeDetails: 'Address Details',
        consignorDetails: 'Address Details',
    };

    // Map section keys to their translation keys (for nested group headers)
    const sectionToHeaderKey: Record<string, string> = {
        shipmentDetailsKeys: 'SHIPMENT_DETAILS',
        taxAndCustomDetailsKeys: 'TAX_AND_CUSTOM_DETAILS',
        packageDetailsKeys: 'PACKAGE_DETAILS',
        codDetailsKeys: 'COD_DETAILS',
        billingDetailsKeys: 'BILLING_DETAILS',
        dangerousGoodKeys: 'DANGEROUS_GOODS_DETAILS',
        pieceDetails: 'PIECE_DETAILS',
        itemDetails: 'Item', // Items are nested within piece details
        consigneeDetails: 'CONSIGNEE_DETAILS',
        consignorDetails: 'CONSIGNOR_DETAILS',
    };

    // Group fields by tab and section
    const tabGroups: Record<string, Array<{ header: string; columns: ColumnInfo[] }>> = {};

    Object.keys(baseKeys).forEach((sectionKey) => {
        const tabHeader = sectionToTabHeader[sectionKey] || 'Primary Details';
        const sectionHeaderKey = sectionToHeaderKey[sectionKey] || sectionKey;
        const sectionHeader = i18n.exists(sectionHeaderKey) ? t(sectionHeaderKey) : sectionHeaderKey;

        if (!tabGroups[tabHeader]) tabGroups[tabHeader] = [];

        const columns = baseKeys[sectionKey]
            .filter((key) => key !== undefined)
            .map((key) => ({
                id: key!,
                key: key!,
                pretty_name: getFieldPrettyName(key!, t, i18n),
                type: 'string',
            }));

        if (columns.length > 0) {
            tabGroups[tabHeader].push({
                header: sectionHeader,
                columns,
            });
        }
    });

    // Convert to metadata format
    return Object.keys(tabGroups).map((tabHeader) => ({
        tab_header: tabHeader,
        details: tabGroups[tabHeader],
    }));
};

/**
 * Print utility functions for consignment labels and invoices
 */

export const handlePrintLabel = (fileBuffer: any, fileName: string): void => {
    if (fileBuffer.isSuccess) {
        const name = fileBuffer.filename || fileName;
        Helper.downloadFileData(
            fileBuffer.data,
            name,
            true,
            'pdf',
        );
        message.success('Success');
    } else {
        message.error('Unable to fetch label at the moment. Please try again after sometime');
    }
};

export const handlePrintInvoiceBuffer = (fileBuffer: any, fileName: string): void => {
    if (fileBuffer.isSuccess) {
        const name = fileBuffer.filename || fileName;
        Helper.downloadFileData(
            fileBuffer.data,
            name,
            true,
            'pdf',
        );
        message.success('Success');
    } else {
        message.error('Unable to fetch Invoice at the moment. Please try again after sometime');
    }
};

export const handlePrint = async (
    referenceNumbers: any,
    isSmall: boolean,
    fileName: string,
    useThirdPartyLabel: boolean,
): Promise<void> => {
    const fileBuffer = await printConsignment({
        isSmall,
        referenceNumbers,
        useThirdPartyLabel,
    });
    handlePrintLabel(fileBuffer, fileName);
};

export const handlePrint4X6 = async (referenceNumbers: any): Promise<void> => {
    const fileUrls = await printConsignment4x6({
        referenceNumbers,
    });
    fileUrls?.data?.forEach((fileUrlsList: any) => {
        fileUrlsList?.forEach(async (fileUrl: any) => {
            if (fileUrl && fileUrl.length) {
                const fileBuffer = await downloadConsignments4X6Files(fileUrl);
                const fileUrlSplit = fileUrl.split('/');
                handlePrintLabel(fileBuffer, fileUrlSplit[fileUrlSplit.length - 1]);
            }
        });
    });
};

export const handlePrint4X6Domestic = async (referenceNumbers: any): Promise<void> => {
    const fileBuffer = await printConsignment4x6Domestic({
        referenceNumbers,
    });
    handlePrintLabel(fileBuffer, `Shipping_Label_4x6_${referenceNumbers[0]}.pdf`);
};

export const handleOnClickPrint4X6 = async (
    referenceNumbers: any,
    isInternational: boolean,
): Promise<void> => {
    if (isInternational) {
        await handlePrint4X6(referenceNumbers);
    } else {
        await handlePrint4X6Domestic(referenceNumbers);
    }
};

export const handlePrintInvoice = async (
    referenceNumbers: any,
    fileName: string,
): Promise<void> => {
    const fileBuffer = await printInvoice({
        referenceNumbers,
    });
    handlePrintInvoiceBuffer(fileBuffer, fileName);
};
